/** tm/components/GovFeedBudget.tsx — AI 총예산 (2026-10-01 메모 6 · 절대 초과 금지)
 *  · BudgetMeter  : 툴바 한 줄 「AI 예산 12.3만 / 130만 원」 + 막대(사용 · 예약 옅게) · 툴팁에 세부
 *  · BudgetBanner : 한도로 멈췄을 때 빨간 띠 + [예산 올리기]
 *  · BudgetBlock  : 「AI 학습」 서랍 안 예산 칸
 *  · BudgetDialog : 예산 올리기 (만원 단위, PUT /gov-feed/budget — 400 문구 그대로)
 */
import { useState } from 'react';
import { C, T, R, Btn } from './ui';
import { usageLines } from './GovFeedParts';
import * as api from '../api';
import type { GovFeedBudget, GovFeedStats } from '../types';

/** 원 → 「12.3만」 · 「130만」 · 「9,800원」 */
export function fmtMan(krw: number | null | undefined): string {
  const v = Math.max(0, Number(krw) || 0);
  if (v < 10000) return `${Math.round(v).toLocaleString()}원`;
  const m = v / 10000;
  return `${m < 100 ? (Math.round(m * 10) / 10).toLocaleString() : Math.round(m).toLocaleString()}만`;
}
const won = (krw: number | null | undefined) => `${Math.round(Number(krw) || 0).toLocaleString()}원`;

export function budgetTip(b: GovFeedBudget, stats?: GovFeedStats | null): string {
  const lines = [
    `사용 ${won(b.spentKrw)} · 예약(일괄 처리 중) ${won(b.reservedKrw)} · 남음 ${won(b.remainingKrw)}`,
    `1건당 추정 ${won(b.perRequestEstKrw)} (${b.perRequestBasis === 'measured' ? `실측 ${b.measuredN}건 기준` : '처음 가정값'})`,
    `남은 분석 ${b.remainingGroups.toLocaleString()}건 포함 전체 예상 ${won(b.projectedTotalKrw)}`,
    `측정 ${b.measuredN}건 · 환율 1달러=${b.rate.toLocaleString()}원(보수적)`,
  ];
  if (b.measuredN < 30) lines.push('처음 100건으로 실제 비용을 재는 중 — 그 뒤 추정이 정확해집니다');
  if (stats) {
    lines.push('', `오늘 AI 분석 ${stats.analyzedToday.toLocaleString()}${stats.dailyUnlimited ? '건 (하루 상한 없음 · 총예산까지)' : `/${stats.dailyMax.toLocaleString()}건`}`);
    if (stats.usage) lines.push(`오늘 (USD)\n${usageLines(stats.usage.today)}`, `누적 (USD)\n${usageLines(stats.usage.total)}`);
  }
  return lines.join('\n');
}

/** 사용 · 예약 · 남음 막대 */
function Bar({ b, h = 6 }: { b: GovFeedBudget; h?: number }) {
  const total = Math.max(1, b.krw);
  const sp = Math.min(100, (b.spentKrw / total) * 100);
  const rs = Math.min(100 - sp, (b.reservedKrw / total) * 100);
  const hot = (b.spentKrw + b.reservedKrw) / total >= 0.9 || b.stopped;
  const col = hot ? C.red : C.accent;
  return (
    <div style={{ position: 'relative', height: h, borderRadius: h / 2, background: C.border, overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${sp}%`, background: col }} />
      <div style={{ position: 'absolute', left: `${sp}%`, top: 0, bottom: 0, width: `${rs}%`, background: col, opacity: 0.35 }} />
    </div>
  );
}

export function BudgetMeter({ b, stats }: { b: GovFeedBudget; stats: GovFeedStats | null }) {
  const used = b.spentKrw + b.reservedKrw;
  const hot = used / Math.max(1, b.krw) >= 0.9 || b.stopped;
  return (
    <span title={budgetTip(b, stats)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0, cursor: 'default', whiteSpace: 'nowrap' }}>
      <span style={{ fontSize: T.xs, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>
        AI 예산 <b style={{ color: hot ? C.red : C.sub, fontWeight: 700 }}>{fmtMan(used)}</b> / {fmtMan(b.krw)} 원
      </span>
      <span style={{ width: 64 }}><Bar b={b} h={5} /></span>
    </span>
  );
}

export function BudgetBanner({ b, pending, onRaise }: { b: GovFeedBudget; pending: number; onRaise: () => void }) {
  const extra = Math.max(0, b.projectedTotalKrw - b.spentKrw - b.reservedKrw);
  return (
    <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', background: C.redLt, borderBottom: `1px solid ${C.red}33`, flexShrink: 0 }}>
      <span style={{ width: 8, height: 8, borderRadius: 4, background: C.red, flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0, fontSize: T.sm, color: C.red, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={b.stopReason || ''}>
        AI 예산 한도({fmtMan(b.krw)} 원)에 닿아 분석을 멈췄습니다
        <span style={{ fontWeight: 400 }}> · 남은 분석 대기 {pending.toLocaleString()}건 · 예상 추가 비용 약 {fmtMan(extra)} 원</span>
      </span>
      <Btn variant="danger" onClick={onRaise} style={{ fontSize: T.xs, padding: '5px 12px', whiteSpace: 'nowrap', background: C.white }}>예산 올리기</Btn>
    </div>
  );
}

export function BudgetBlock({ b, onEdit }: { b: GovFeedBudget; onEdit: () => void }) {
  const cell = (label: string, v: string, strong?: boolean, color?: string) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: T.xs, color: C.sub }}>{label}</div>
      <div style={{ fontSize: strong ? T.lg : T.md, fontWeight: 700, color: color || C.text, fontVariantNumeric: 'tabular-nums', marginTop: 2, whiteSpace: 'nowrap' }}>{v}</div>
    </div>
  );
  return (
    <div style={{ padding: '12px 14px', borderRadius: R.md, border: `1px solid ${b.stopped ? `${C.red}55` : C.border}`, background: b.stopped ? C.redLt : C.white }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: T.sm, fontWeight: 700, color: C.text }}>총예산 {won(b.krw)}</span>
        {b.stopped && <span style={{ fontSize: T.xs, fontWeight: 700, color: C.red }}>한도로 멈춤</span>}
        <span style={{ flex: 1 }} />
        <button onClick={onEdit} style={{ height: 26, padding: '0 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', fontSize: T.xs, color: C.sub, fontWeight: 600 }}>예산 올리기</button>
      </div>
      <Bar b={b} h={8} />
      <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
        {cell('사용', fmtMan(b.spentKrw), true)}
        {cell('예약(일괄 중)', fmtMan(b.reservedKrw))}
        {cell('남음', fmtMan(b.remainingKrw), false, b.remainingKrw / Math.max(1, b.krw) < 0.1 ? C.red : C.green)}
        {cell('전체 예상', fmtMan(b.projectedTotalKrw), false, b.projectedTotalKrw > b.krw ? '#B45309' : C.text)}
      </div>
      <div style={{ fontSize: T.xs, color: C.muted, marginTop: 8, lineHeight: 1.6 }}>
        1건당 추정 {won(b.perRequestEstKrw)} ({b.perRequestBasis === 'measured' ? `실측 ${b.measuredN}건` : '처음 가정값'}) · 남은 분석 {b.remainingGroups.toLocaleString()}건 · 1달러={b.rate.toLocaleString()}원(보수적)
        {b.measuredN < 30 && <><br />처음 100건으로 실제 비용을 재는 중입니다</>}
      </div>
    </div>
  );
}

export function BudgetDialog({ b, onClose, onSaved }: { b: GovFeedBudget; onClose: () => void; onSaved: (nb: GovFeedBudget) => void }) {
  const minMan = Math.ceil((b.spentKrw + b.reservedKrw) / 10000);
  const suggest = Math.max(Math.ceil(b.krw / 10000), Math.ceil((b.projectedTotalKrw * 1.1) / 10000));
  const [man, setMan] = useState(String(suggest));
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const v = Number(man);
  async function save() {
    if (!Number.isFinite(v) || v <= 0) { setErr('금액을 만원 단위로 입력하세요'); return; }
    setBusy(true); setErr('');
    try { const r = await api.setGovFeedBudget({ krw: Math.round(v * 10000) }); if (r.data) onSaved(r.data); }
    catch (e) { setErr(e instanceof Error ? e.message : '저장 실패'); }
    finally { setBusy(false); }
  }
  const row = (k: string, val: string, color?: string) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: T.sm, padding: '4px 0' }}>
      <span style={{ color: C.sub }}>{k}</span><span style={{ fontWeight: 600, color: color || C.text, fontVariantNumeric: 'tabular-nums' }}>{val}</span>
    </div>
  );
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1250, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
      <div role="dialog" aria-modal="true" style={{ position: 'relative', width: '100%', maxWidth: 380, background: C.white, borderRadius: R.lg, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', background: 'linear-gradient(135deg, #111827, #1B3A5C)', color: '#fff', fontSize: T.lg, fontWeight: 700 }}>AI 예산 올리기</div>
        <div style={{ padding: '16px 20px' }}>
          {row('지금 예산', won(b.krw))}
          {row('이미 사용', won(b.spentKrw))}
          {row('예약(일괄 처리 중)', won(b.reservedKrw))}
          {row('남은 분석까지 전체 예상', won(b.projectedTotalKrw), b.projectedTotalKrw > b.krw ? '#B45309' : undefined)}
          <div style={{ fontSize: T.xs, fontWeight: 600, color: C.sub, marginTop: 14, marginBottom: 6 }}>새 총예산</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input autoFocus type="number" min={minMan} value={man} onChange={e => { setMan(e.target.value); setErr(''); }}
              onKeyDown={e => { if (e.key === 'Enter') void save(); if (e.key === 'Escape') onClose(); }}
              style={{ width: 140, height: 38, padding: '0 12px', borderRadius: 8, border: `1px solid ${err ? C.red : C.border}`, fontSize: T.md, outline: 'none', boxSizing: 'border-box', fontVariantNumeric: 'tabular-nums' }} />
            <span style={{ fontSize: T.sm, color: C.sub }}>만원</span>
            <span style={{ fontSize: T.xs, color: C.muted, marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>{Number.isFinite(v) && v > 0 ? won(v * 10000) : ''}</span>
          </div>
          <div style={{ fontSize: T.xs, color: err ? C.red : C.muted, marginTop: 8, lineHeight: 1.5 }}>
            {err || `사용+예약(${fmtMan(b.spentKrw + b.reservedKrw)} 원)보다 커야 합니다 · 저장하면 멈춘 분석을 다시 시작합니다`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: '12px 20px', borderTop: `1px solid ${C.border}` }}>
          <button onClick={onClose} style={{ flex: 1, height: 38, borderRadius: R.md, border: `1px solid ${C.border}`, background: C.white, color: C.sub, fontSize: T.sm, fontWeight: 600, cursor: 'pointer' }}>취소</button>
          <button onClick={save} disabled={busy} style={{ flex: 2, height: 38, borderRadius: R.md, border: 'none', background: `linear-gradient(135deg,${C.accent},${C.primary})`, color: '#fff', fontSize: T.md, fontWeight: 700, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>{busy ? '저장 중…' : '저장하고 다시 시작'}</button>
        </div>
      </div>
    </div>
  );
}
